/**
 * Explore Mode state machine + persistence (no React, no DOM) — kept tiny
 * because App.tsx imports `runLegacyTourCheck` into the boot path.
 *
 *   idle ──(conditions met)──▶ welcome ──start──▶ exploring ──finish──▶ done
 *                                  └────skip────▶ skipped
 *   any ──restart──▶ exploring (explored: [], no welcome)
 *
 * `loading` precedes the first storage read. `welcome` is never persisted: a
 * tab closed on the welcome card simply shows it again next time.
 */
import { LAST_AUTH_UID_KEY, loadFromStorageAsync, saveToStorageAsync } from '../../../services/storage';
import type { TourStepId } from './tourSteps';

/** Device-scoped (see the storage.ts contract comment), NOT user-scoped. */
export const TOUR_STORAGE_KEY = 'tour.explore.v1';

export type TourStatus = 'loading' | 'idle' | 'welcome' | 'exploring' | 'done' | 'skipped';

export interface TourRecord {
  status: 'exploring' | 'done' | 'skipped';
  explored: TourStepId[];
  /** Written by the legacy check: this device used Rhemn before the tour shipped. */
  legacy?: boolean;
}

export interface TourState {
  status: TourStatus;
  explored: TourStepId[];
}

export type TourAction =
  | { type: 'loaded'; record: TourRecord | null }
  | { type: 'showWelcome' }
  | { type: 'start' }
  | { type: 'skip' }
  | { type: 'markExplored'; id: TourStepId }
  | { type: 'finish' }
  | { type: 'restart' };

export const INITIAL_TOUR_STATE: TourState = { status: 'loading', explored: [] };

const isRecord = (value: unknown): value is TourRecord => {
  if (!value || typeof value !== 'object') return false;
  const { status, explored } = value as Partial<TourRecord>;
  return (status === 'exploring' || status === 'done' || status === 'skipped')
    && (explored === undefined || Array.isArray(explored));
};

export const tourReducer = (state: TourState, action: TourAction): TourState => {
  switch (action.type) {
    case 'loaded':
      if (state.status !== 'loading') return state;
      return action.record
        ? { status: action.record.status, explored: action.record.explored ?? [] }
        : { status: 'idle', explored: [] };
    case 'showWelcome':
      return state.status === 'idle' ? { ...state, status: 'welcome' } : state;
    case 'start':
      return state.status === 'welcome' || state.status === 'idle'
        ? { status: 'exploring', explored: [] }
        : state;
    case 'skip':
      return state.status === 'welcome' ? { ...state, status: 'skipped' } : state;
    case 'markExplored':
      if (state.status !== 'exploring' || state.explored.includes(action.id)) return state;
      return { ...state, explored: [...state.explored, action.id] };
    case 'finish':
      return state.status === 'exploring' ? { ...state, status: 'done' } : state;
    case 'restart':
      return { status: 'exploring', explored: [] };
    default:
      return state;
  }
};

/** The persisted form of a state, or null for states that are never written. */
export const toRecord = (state: TourState): TourRecord | null =>
  state.status === 'exploring' || state.status === 'done' || state.status === 'skipped'
    ? { status: state.status, explored: state.explored }
    : null;

export const loadTourRecord = async (): Promise<TourRecord | null> => {
  const raw = await loadFromStorageAsync<unknown>(TOUR_STORAGE_KEY, null);
  return isRecord(raw) ? raw : null;
};

export const saveTourRecord = (record: TourRecord): Promise<void> =>
  saveToStorageAsync(TOUR_STORAGE_KEY, record);

/**
 * Legacy check (plan §2.2). Must run at boot BEFORE auth resolves: AppContext's
 * identity guard stamps `auth.lastUid` on every sign-in, so reading it after
 * that stamp would mark every brand-new user as legacy. App.tsx calls this in a
 * render-phase initializer, which precedes every effect in the tree.
 *
 * Synchronous on purpose: localStorage is storage.ts's primary store, and
 * saveToStorageAsync writes it before its first await.
 */
export const runLegacyTourCheck = (): void => {
  try {
    if (localStorage.getItem(TOUR_STORAGE_KEY) !== null) return;
    if (localStorage.getItem(LAST_AUTH_UID_KEY) === null) return;
  } catch {
    return; // storage unavailable: the tour simply stays eligible
  }
  const record: TourRecord = { status: 'done', explored: [], legacy: true };
  void saveTourRecord(record);
};
